import React, { useRef } from 'react';
import { Folder, GitBranch, Plus, Loader2, AlertCircle, X } from 'lucide-react';
import FileRow from './FileRow';

/**
 * FilesTree — Project Files + per-version folders, always expanded.
 * Each section has:
 *   - title + count + (+ Add doc) button on the header
 *   - file rows (or empty hint)
 *   - drop zone (the entire section is a drop target — see ProjectDetail wiring)
 *
 * Upload click + drop callbacks are passed in by ProjectDetail. This
 * component is presentational; the upload pipeline lives in
 * lib/uploadProjectFile.js and the dispatching state lives in ProjectDetail.
 */
export default function FilesTree({
  project = {},
  projectId,
  onPromoted,
  onDetach,
  onAddFile,        // (scope, FileList) => void
  uploadingByScope = {},  // { 'project': 2, 'fray-f1': 1 }
  // #1221: per-file upload state. Maps scope key → list of
  // { filename, status: 'uploading'|'done'|'failed', error }.
  // The UI renders an in-progress / failed row per file inside the
  // section so users can see WHAT is uploading and WHY any failed.
  uploadsByScope = {},
  clearUploadStatus,    // (scope, filename) => void — dismiss failed row
}) {
  const projectContexts = project.contexts || [];
  const frays = project.frays || [];
  const projectInputRef = useRef(null);

  const projectKey = 'project';
  const projectUploading = uploadingByScope[projectKey] || 0;
  const projectUploads = uploadsByScope[projectKey] || [];

  const onPickProject = () => projectInputRef.current?.click();
  const onChangeProject = (e) => {
    if (e.target.files && e.target.files.length > 0 && onAddFile) {
      onAddFile({ type: 'project', projectId }, e.target.files);
      e.target.value = '';
    }
  };
  const onDropProject = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && onAddFile) {
      onAddFile({ type: 'project', projectId }, e.dataTransfer.files);
    }
  };
  const onDragOver = (e) => e.preventDefault();

  return (
    <div className="space-y-3 text-xs font-mono" data-testid="files-tree">
      <Section
        title="Project Files"
        icon={<Folder className="w-3.5 h-3.5 text-[#F2A900] shrink-0" />}
        count={projectContexts.length}
        uploading={projectUploading}
        onAddDoc={onPickProject}
        addDocTestId="add-doc-project"
        onDrop={onDropProject}
        onDragOver={onDragOver}
      >
        {projectContexts.length === 0 && projectUploads.length === 0 ? (
          <div className="text-[#A0A0A0] italic py-1">No files yet — auto-flow into every fray when added.</div>
        ) : (
          projectContexts.map((c) => (
            <FileRow
              key={c.context_id}
              context={{ ...c, source: c.source || 'project' }}
              projectId={projectId}
              onDetach={onDetach}
            />
          ))
        )}
        {/* #1221: per-file upload status rows — uploading + failed */}
        {projectUploads.filter((u) => u.status !== 'done').map((u) => (
          <UploadStatusRow
            key={`${u.filename}-${u.startedAt}`}
            upload={u}
            onDismiss={() =>
              clearUploadStatus &&
              clearUploadStatus({ type: 'project', projectId }, u.filename)
            }
          />
        ))}
        <DropHint />
        <input
          ref={projectInputRef}
          type="file"
          multiple
          onChange={onChangeProject}
          className="hidden"
          data-testid="add-doc-project-input"
        />
      </Section>

      {frays.map((f) => (
        <FraySection
          key={f.id}
          fray={f}
          projectId={projectId}
          uploading={uploadingByScope[`fray-${f.id}`] || 0}
          uploads={uploadsByScope[`fray-${f.id}`] || []}
          clearUploadStatus={clearUploadStatus}
          onAddFile={onAddFile}
          onPromoted={onPromoted}
          projectContexts={projectContexts}
        />
      ))}
    </div>
  );
}

function Section({
  title, icon, count, uploading, onAddDoc, addDocTestId,
  onDrop, onDragOver, children,
}) {
  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      className="bg-[#1E1E1E] border border-[#2A2A2A] hover:border-[#3A3A3A] rounded-lg p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="font-bold uppercase tracking-wider text-[#F5F1E8]">{title}</span>
        <span className="text-[#A0A0A0]">({count})</span>
        <span className="flex-1" />
        {uploading > 0 && (
          <span className="text-[10px] text-[#F2A900] italic mr-2">uploading {uploading}…</span>
        )}
        <button
          type="button"
          onClick={onAddDoc}
          data-testid={addDocTestId}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-[#A0A0A0] hover:text-[#F5F1E8] border border-[#2A2A2A] hover:border-[#FF4D00] rounded transition-colors"
        >
          <Plus className="w-3 h-3" />
          Add doc
        </button>
      </div>
      <div className="ml-1 border-l border-[#2A2A2A] pl-3 space-y-1">
        {children}
      </div>
    </div>
  );
}

function DropHint() {
  return (
    <div className="text-[10px] text-[#555] italic mt-2 pt-2 border-t border-dashed border-[#2A2A2A]">
      Drag files here, or click + Add doc
    </div>
  );
}

function FraySection({ fray, projectId, uploading, uploads = [], clearUploadStatus, onAddFile, onPromoted, projectContexts }) {
  const inputRef = useRef(null);
  const frayContexts = fray.fray_contexts || [];
  const label = fray.template_name || (fray.id || '').slice(0, 8);

  const onPick = () => inputRef.current?.click();
  const onChange = (e) => {
    if (e.target.files && e.target.files.length > 0 && onAddFile) {
      onAddFile({ type: 'fray', frayId: fray.id }, e.target.files);
      e.target.value = '';
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && onAddFile) {
      onAddFile({ type: 'fray', frayId: fray.id }, e.dataTransfer.files);
    }
  };
  const onDragOver = (e) => e.preventDefault();

  return (
    <Section
      title={label}
      icon={<GitBranch className="w-3.5 h-3.5 text-[#A0A0A0] shrink-0" />}
      count={frayContexts.length}
      uploading={uploading}
      onAddDoc={onPick}
      addDocTestId={`add-doc-fray-${fray.id}`}
      onDrop={onDrop}
      onDragOver={onDragOver}
    >
      {frayContexts.length === 0 && uploads.length === 0 ? (
        <div className="text-[#A0A0A0] italic py-1">No fray-specific files.</div>
      ) : (
        frayContexts.map((c) => (
          <FileRow
            key={c.context_id}
            context={{ ...c, source: c.source || 'fray' }}
            projectId={projectId}
            frayId={fray.id}
            onPromoted={onPromoted}
          />
        ))
      )}
      {/* #1221: per-file upload status rows for this fray's scope */}
      {uploads.filter((u) => u.status !== 'done').map((u) => (
        <UploadStatusRow
          key={`${u.filename}-${u.startedAt}`}
          upload={u}
          onDismiss={() =>
            clearUploadStatus &&
            clearUploadStatus({ type: 'fray', frayId: fray.id }, u.filename)
          }
        />
      ))}
      <EffectiveFilesFooter
        frayId={fray.id}
        projectCount={projectContexts.length}
        frayCount={frayContexts.length}
      />
      <DropHint />
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={onChange}
        className="hidden"
        data-testid={`add-doc-fray-${fray.id}-input`}
      />
    </Section>
  );
}

function EffectiveFilesFooter({ frayId, projectCount, frayCount }) {
  return (
    <div
      data-testid={`effective-files-${frayId}`}
      className="mt-1 px-2 py-1 text-[10px] text-[#A0A0A0] italic"
    >
      effective files: {projectCount} project + {frayCount} fray-specific = {projectCount + frayCount}
    </div>
  );
}

/**
 * #1221: per-file upload status row. Renders inline in the section
 * during the upload + on failure. Pre-fix the UI showed only an
 * aggregate "uploading N…" hint and silently swallowed failures —
 * users couldn't tell what was happening or what went wrong.
 */
function UploadStatusRow({ upload, onDismiss }) {
  const { filename, status, error } = upload;
  if (status === 'uploading') {
    return (
      <div
        className="flex items-center gap-2 py-1 px-2 text-[#F2A900]"
        data-testid={`upload-row-${filename}`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
        <span className="truncate">{filename}</span>
        <span className="text-[10px] italic text-[#A0A0A0] ml-auto">
          uploading…
        </span>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div
        className="flex items-start gap-2 py-1 px-2 text-[#A61E17] bg-[#1A0A08] border border-[#A61E17]/40 rounded mt-1"
        data-testid={`upload-row-failed-${filename}`}
      >
        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="truncate font-bold">{filename}</div>
          {error && (
            <div className="text-[10px] text-[#FF7A6E] break-words">
              {error}
            </div>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-[#A0A0A0] hover:text-[#F5F1E8] shrink-0"
            data-testid={`upload-dismiss-${filename}`}
            aria-label="Dismiss failed upload"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }
  return null;
}
